import React, { useCallback, useContext, useEffect, useState } from 'react';

import GlobalToolbar from '../global-toolbar/global-toolbar';
import SubsiteNavDesktop from '../navigation/subsite-nav-desktop/subsite-nav-desktop';
import SubsiteNavMobile from '../navigation/subsite-nav-mobile/subsite-nav-mobile';
import PrimaryHeader from '../primary-header/primary-header';
import CondensedPrimaryHeader from '../condensed-primary-header/condensed-primary-header';
import GlobalAlerts from '../shared/components/global-alerts/global-alerts';
import { AppDataContext } from '../shared/contexts/app-data/AppDataContext';
import { Media } from '../shared/contexts/media/MediaContext';
import { UiStateContext } from '../shared/contexts/ui-state/UiStateContext';
import useFeatureToggle from '../shared/hooks/use-feature-toggle/useFeatureToggle';
import {
  FeatureToggle,
  GlobalNavContent,
  GlobalToolbarContent,
  IconPack,
  PrimaryHeaderContent,
  SubsiteContent
} from '../shared/models/app-data-model';
import UserLocationPrompt from '../user-location-prompt/user-location-prompt';
import './sticky-header.scss';

export type StickyHeaderProps = {
  featureToggles: FeatureToggle[];
  globalNavContent: GlobalNavContent;
  primaryHeaderContent: PrimaryHeaderContent;
  staticIcons: IconPack[];
  title: string;
  subsiteData?: SubsiteContent;
  dynamicPrimaryHeader?: boolean;
  lightFontMode?: boolean;
  campaignPageActive?: boolean;
  globalToolbarContent?: GlobalToolbarContent;
  corewellStyleOverrides?: boolean;
};

const StickyHeader: React.FC<StickyHeaderProps> = ({
  featureToggles,
  globalNavContent,
  primaryHeaderContent,
  staticIcons,
  subsiteData,
  campaignPageActive,
  dynamicPrimaryHeader,
  lightFontMode,
  title,
  globalToolbarContent,
  corewellStyleOverrides
}: StickyHeaderProps) => {
  const context = useContext(AppDataContext);
  const uiStateContext = useContext(UiStateContext);
  const hidingHeaderFeatureEnabled = useFeatureToggle('header-hiding-on-scroll', featureToggles);
  const skipToMainFeatureEnabled = useFeatureToggle('skip-to-main-link', featureToggles);
  const condensedHeaderFeatureEnabled = useFeatureToggle('condensed-primary-header', featureToggles);
  const showAlerts = uiStateContext.showAlerts;
  const [menusOpen, setMenusOpen] = useState(uiStateContext.menuOpen || uiStateContext.auxNavOpen || uiStateContext.subsiteMenuOpen);
  const [headerVisibility, setHeaderVisibility] = useState(false);
  const [alertsVisibility, setAlertsVisibility] = useState(true);
  const [userFocusedOnHeader, setUserFocusedOnHeader] = useState(false);

  /**
   * This handler handles when the user hovers on the header
   */
  const userHoveredOnHeaderHandler = useCallback(() => {
    setUserFocusedOnHeader(true);
  }, [setUserFocusedOnHeader]);

  /**
   * This handler handles when the user leaves hovering on the header
   */
  const userHoverLeftHeaderHandler = useCallback(() => {
    setUserFocusedOnHeader(false);
  }, [setUserFocusedOnHeader]);

  /**
   * This effect controls the state of our header & alerts visibility
   * And that state triggers the animations for the header and/or alerts sliding up out of view.
   */
  useEffect(() => {
    if (!hidingHeaderFeatureEnabled) {
      return;
    }

    setHeaderVisibility(
      !uiStateContext.pageHasScrolled || userFocusedOnHeader || !context?.subsiteData || !context.subsiteData?.subsite_nav
    );
    setAlertsVisibility(!uiStateContext.pageHasScrolled && !menusOpen);
  }, [context, hidingHeaderFeatureEnabled, menusOpen, uiStateContext.pageHasScrolled, userFocusedOnHeader]);

  /**
   * This effect checks if our menus are open and sets our menusOpen bool
   */
  useEffect(() => {
    setMenusOpen(uiStateContext.menuOpen || uiStateContext.auxNavOpen || uiStateContext.subsiteMenuOpen);
  }, [setMenusOpen, uiStateContext.menuOpen, uiStateContext.auxNavOpen, uiStateContext.subsiteMenuOpen]);

  const subsiteNav =
    subsiteData && !campaignPageActive ? (
      <div hidden={uiStateContext.auxNavOpen || uiStateContext.menuOpen}>
        <Media lessThan="desktop">
          <SubsiteNavMobile content={subsiteData} featureToggles={featureToggles} iconPack={staticIcons} />
        </Media>
        <Media greaterThan="tablet">
          <SubsiteNavDesktop content={subsiteData} featureToggles={featureToggles} iconPack={staticIcons} />
        </Media>
      </div>
    ) : null;

  const primaryHeader = primaryHeaderContent && (
    condensedHeaderFeatureEnabled && corewellStyleOverrides ? (
      <CondensedPrimaryHeader
        iconPack={staticIcons}
        featureToggles={featureToggles}
        headerContent={primaryHeaderContent}
        globalToolbarContent={globalToolbarContent}
        navContent={globalNavContent}
        campaignPageActive={campaignPageActive}
        title={title} />
    ) : (
      <PrimaryHeader
        iconPack={staticIcons}
        featureToggles={featureToggles}
        headerContent={primaryHeaderContent}
        navContent={globalNavContent}
        visible={headerVisibility || menusOpen}
        subsitePresent={!!subsiteNav}
        dynamicPrimaryHeader={dynamicPrimaryHeader}
        campaignPageActive={campaignPageActive}
        lightFontMode={lightFontMode}
        title={title}
      />
    ));

  const skipToMainLink = skipToMainFeatureEnabled ? (
    <a className="skip-to-content visually-hidden" href="#page-content">
      Skip to Content
    </a>
  ) : null;

  return (
    <div
      className="sticky-header"
      id="sticky-header"
      data-testid={`sticky-header${campaignPageActive ? '-campaign-page' : ''}`}
      onMouseEnter={userHoveredOnHeaderHandler}
      onFocus={userHoveredOnHeaderHandler}
      onMouseLeave={userHoverLeftHeaderHandler}
    >
      {skipToMainLink}
      <div className={`alerts-wrapper ${alertsVisibility && showAlerts ? `expand` : `collapse`}`} id="alert-container">
        <UserLocationPrompt featureToggles={featureToggles} iconPack={staticIcons} />
        <GlobalAlerts iconPack={staticIcons} featureToggles={featureToggles} />
      </div>
      {globalToolbarContent && !campaignPageActive && (
        <Media greaterThanOrEqual="desktop">
          <GlobalToolbar content={globalToolbarContent} featureToggles={featureToggles} iconPack={staticIcons} />
        </Media>
      )}
      {primaryHeader}
      {subsiteNav}
    </div>
  );
};

export default StickyHeader;
